import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class PushSubscriptionService {
    private readonly http = inject(HttpClient);

    readonly isSupported = signal(this.checkSupport());
    readonly isSubscribed = signal(false);

    private registration: ServiceWorkerRegistration | null = null;

    async subscribe(): Promise<boolean> {
        if (!this.isSupported()) {
            return false;
        }

        try {
            this.registration = await navigator.serviceWorker.register('/push-sw.js');
            await navigator.serviceWorker.ready;

            const vapidResponse = await firstValueFrom(
                this.http.get<{ vapid_public_key: string }>(`${environment.apiBase}/user/vapid-public-key`)
            );

            if (!vapidResponse.vapid_public_key) {
                return false;
            }

            const subscription = await this.registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: this.urlBase64ToUint8Array(vapidResponse.vapid_public_key) as BufferSource,
            });

            const raw = subscription.toJSON();
            await firstValueFrom(
                this.http.post(`${environment.apiBase}/user/push-subscription`, {
                    endpoint: raw.endpoint,
                    keys: raw.keys,
                })
            );

            this.isSubscribed.set(true);
            return true;
        } catch {
            return false;
        }
    }

    async unsubscribe(): Promise<void> {
        try {
            if (this.registration) {
                const subscription = await this.registration.pushManager.getSubscription();
                if (subscription) {
                    await firstValueFrom(
                        this.http.delete(`${environment.apiBase}/user/push-subscription`, {
                            body: { endpoint: subscription.endpoint },
                        })
                    );
                    await subscription.unsubscribe();
                }
            }
        } catch {
            // Best-effort cleanup
        }
        this.isSubscribed.set(false);
    }

    async checkExistingSubscription(): Promise<void> {
        if (!this.isSupported()) {
            return;
        }
        try {
            const reg = await navigator.serviceWorker.getRegistration('/push-sw.js');
            if (reg) {
                this.registration = reg;
                const sub = await reg.pushManager.getSubscription();
                this.isSubscribed.set(sub !== null);
            }
        } catch {
            // No existing registration
        }
    }

    private checkSupport(): boolean {
        return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    }

    private urlBase64ToUint8Array(base64String: string): Uint8Array {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }
}
